import React from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import { FaCheck } from 'react-icons/fa'
import { useScreen } from '@root/utils'
import cn from 'classnames'

interface Props {
  availableColors: string[]
  selectedColor: string
  setSelectedColor: (color: string) => void
}

const ColorPicker: React.FC<Props> = ({ availableColors, selectedColor, setSelectedColor }) => {
  const [t] = useTranslation()
  const { isMobile } = useScreen()

  return (
    <ColorPickerContainer>
      <ColorPickerLabel>{t('settings.tags.form.color')}</ColorPickerLabel>
      <ColorPickerOptions className={cn({ isMobile })}>
        {availableColors.map(color => (
          <ColorOption
            key={color}
            $color={color}
            $selected={selectedColor === color}
            onClick={() => setSelectedColor(color)}
            type="button"
          >
            <FaCheck />
          </ColorOption>
        ))}
      </ColorPickerOptions>
    </ColorPickerContainer>
  )
}

export default ColorPicker

const ColorPickerContainer = styled.div`
  margin-top: ${({ theme }) => theme.spacing.sm}rem;
`

const ColorPickerLabel = styled.p`
  margin-bottom: ${({ theme }) => theme.spacing.sm}rem;
  font-size: ${({ theme }) => theme.fontSize.md}rem;
  font-weight: bold;
  color: ${({ theme }) => theme.colors.metalGray};
`

const ColorPickerOptions = styled.div`
  display: grid;
  grid-template-columns: repeat(7, 42px);
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing.sm}rem;
  width: 100%;

  &.isMobile {
    grid-template-columns: repeat(auto-fit, 42px);
  }
`

const ColorOption = styled.button<{ $color: string; $selected: boolean }>`
  width: 42px;
  height: 42px;
  border-radius: 50%;
  border: none;
  background-color: ${({ $color }) => $color};
  box-shadow: ${({ $selected, theme, $color }) =>
    $selected ? `0 0 0 2px ${theme.colors.neutralWhite}, 0 0 0 5px ${$color}` : 'none'};
  cursor: pointer;
  transition: transform 0.2s, box-shadow 0.2s;
  padding: 0;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    transform: scale(1.15);
  }

  ${({ $selected, theme }) =>
    $selected &&
    `
        svg {
            color: ${theme.colors.neutralWhite};
            font-size: 20px;
        }
    `}

  ${({ $selected }) =>
    !$selected &&
    `
        svg {
            display: none;
        }
    `}
`
